'use client'

import { useForm } from 'react-hook-form'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { Button } from '@/General/Button'
import { useTurnstile } from '@/Turnstile'
import { useSubmitConsultingFormMutation } from '@/generated/graphql'
import { useRouter } from 'next/navigation'
import { useRef } from 'react'
import { EMAIL_REGEX } from '@/validation'

interface ConsultingFormData {
  name: string
  companyName: string
  email: string
  details: string
}

export const ConsultingForm = () => {
  const { executeTurnstile } = useTurnstile()
  const pendingThankYouRef = useRef<ConsultingFormData | null>(null)
  const router = useRouter()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ConsultingFormData>({
    mode: 'onSubmit',
    defaultValues: { name: '', companyName: '', email: '', details: '' },
  })

  const { mutate: submitConsultingForm } = useSubmitConsultingFormMutation({
    onSuccess: (result) => {
      const pending = pendingThankYouRef.current
      if (result.submitConsultingForm && pending) {
        pendingThankYouRef.current = null
        const params = new URLSearchParams({
          email: pending.email,
          name: pending.name
        })
        router.push(`/magic-consulting/thank-you?${params.toString()}`)
      }
    },
    onError: () => {
      setError('root', {
        type: 'manual',
        message: 'Something went wrong. Please try again later or email us at magic@vanishingincmagic.com.',
      })
    },
  })

  const onSubmit = async (data: ConsultingFormData) => {
    try {
      const token = await executeTurnstile()
      const response = await fetch('/api/verifycaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })

      if (!response.ok) {
        setError('root', {
          type: 'manual',
          message:
            'Unfortunately we were unable to verify that a human was submitting this form. Please try emailing us at magic@vanishingincmagic.com instead.',
        })
        return
      }

      pendingThankYouRef.current = data
      submitConsultingForm({
        name: data.name,
        companyName: data.companyName,
        email: data.email,
        details: data.details
      })
    } catch {
      setError('root', {
        type: 'manual',
        message: 'Something went wrong. Please try again later or email us at magic@vanishingincmagic.com.',
      })
    }
  }

  return (
    <form
      className="space-y-6 my-4"
      onSubmit={e => void handleSubmit(onSubmit)(e)}
      noValidate
    >
      <ErrorBox
        errors={
          Object.values(errors)
            .map(error => error?.message)
            .filter(Boolean) as string[]
        }
      />

      {/* Name Field */}
      <div className="space-y-2">
        <label htmlFor="name" className="block text-black">
          Your name:
        </label>
        <input
          {...register('name', { required: 'Name is required' })}
          type="text"
          id="name"
          className={`w-full p-2 border ${
            errors.name
              ? 'border-red-500 focus:ring-red-300'
              : 'border-gray-300'
          } rounded shadow-sm focus:outline-hidden focus:ring-3 focus:ring-blue-200 text-black`}
        />
        {errors.name && (
          <p className="text-red-500 text-sm">{errors.name.message}</p>
        )}
      </div>

      {/* Company Name Field */}
      <div className="space-y-2">
        <label htmlFor="companyName" className="block text-black">
          Company name (optional):
        </label>
        <input
          {...register('companyName')}
          type="text"
          id="companyName"
          className="w-full p-2 border border-gray-300 rounded shadow-sm focus:outline-hidden focus:ring-3 focus:ring-blue-200 text-black"
        />
      </div>

      {/* Email Field */}
      <div className="space-y-2">
        <label htmlFor="email" className="block text-black">
          Your email address:
        </label>
        <input
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: EMAIL_REGEX,
              message: 'Invalid email address',
            },
          })}
          type="email"
          id="email"
          className={`w-full p-2 border ${
            errors.email
              ? 'border-red-500 focus:ring-red-300'
              : 'border-gray-300'
          } rounded shadow-sm focus:outline-hidden focus:ring-3 focus:ring-blue-200 text-black`}
        />
        {errors.email && (
          <p className="text-red-500 text-sm">{errors.email.message}</p>
        )}
      </div>

      {/* Details Field */}
      <div className="space-y-2">
        <label htmlFor="details" className="block text-black">
          Details about your project:
        </label>
        <textarea
          {...register('details', { required: 'Please enter details about your project' })}
          id="details"
          rows={8}
          className={`w-full p-2 border ${
            errors.details
              ? 'border-red-500 focus:ring-red-300'
              : 'border-gray-300'
          } rounded shadow-sm focus:outline-hidden focus:ring-3 focus:ring-blue-200 text-black`}
        />
        {errors.details && (
          <p className="text-red-500 text-sm">{errors.details.message}</p>
        )}
      </div>

      {/* Submit Button */}
      <div className="text-right">
                <Button type="submit">
          Submit enquiry
        </Button>
      </div>
    </form>
  )
}
